import { useRef, useState } from "react";
import { API, MODELS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ScanSearch, ShieldCheck, Loader2, AlertTriangle, Sparkles, Bot, Repeat, Lightbulb, Printer, Upload, FileText, CheckCircle2, XCircle, MessageSquare } from "lucide-react";

const FLAG_META = (score) => {
  if (score >= 80) return { level: "green", label: "Clear — looks original",  color: "emerald", Icon: CheckCircle2 };
  if (score >= 60) return { level: "amber", label: "Caution — some rework advised", color: "amber",   Icon: AlertTriangle };
  return { level: "red", label: "Rework required — high originality risk", color: "red",    Icon: XCircle };
};

const RING_COLORS = (score) => {
  if (score >= 80) return "text-emerald-600";
  if (score >= 60) return "text-amber-600";
  if (score >= 40) return "text-orange-600";
  return "text-red-600";
};

function ScoreRing({ score, label, sublabel, invert = false }) {
  const s = Math.max(0, Math.min(100, Number(score) || 0));
  const displayScore = invert ? 100 - s : s;
  const color = RING_COLORS(displayScore);
  const circ = 2 * Math.PI * 46;
  const dash = (s / 100) * circ;
  return (
    <div className="flex flex-col items-center" data-testid={`plag-ring-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <div className="relative w-32 h-32">
        <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
          <circle cx="50" cy="50" r="46" strokeWidth="8" className="stroke-secondary" fill="none"/>
          <circle cx="50" cy="50" r="46" strokeWidth="8" fill="none"
            className={`transition-all duration-1000 ${color}`}
            stroke="currentColor" strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"/>
        </svg>
        <div className={`absolute inset-0 flex flex-col items-center justify-center ${color}`}>
          <div className="font-display text-4xl">{s}</div>
          <div className="text-[10px] uppercase tracking-widest opacity-70">/ 100</div>
        </div>
      </div>
      <div className="mt-3 text-center">
        <div className="font-medium">{label}</div>
        <div className="text-xs text-muted-foreground">{sublabel}</div>
      </div>
    </div>
  );
}

export default function Plagiarism() {
  const [text, setText] = useState("");
  const [docType, setDocType] = useState("proposal");
  const [modelIdx, setModelIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [inputMode, setInputMode] = useState("paste"); // "paste" | "upload"
  const [uploadName, setUploadName] = useState("");
  const fileRef = useRef(null);

  const run = async () => {
    if (text.trim().length < 60) return toast.error("Please paste at least 60 characters.");
    setLoading(true); setReport(null);
    try {
      const m = MODELS[modelIdx];
      const res = await fetch(`${API}/plagiarism/check`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("gf_token")}` },
        body: JSON.stringify({ text, doc_type: docType, model_provider: m.provider, model_name: m.name }),
      });
      if (!res.ok) { const err = await res.json().catch(()=>({})); throw new Error(err.detail || "Check failed"); }
      setReport(await res.json());
      toast.success("Originality analysis complete");
    } catch (e) { toast.error(e.message || "Check failed"); }
    finally { setLoading(false); }
  };

  const runFile = async (file) => {
    if (!file) return;
    setUploadName(file.name);
    setLoading(true); setReport(null);
    try {
      const m = MODELS[modelIdx];
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", docType);
      fd.append("model_provider", m.provider);
      fd.append("model_name", m.name);
      const res = await fetch(`${API}/plagiarism/check-file`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("gf_token")}` },
        body: fd,
      });
      if (!res.ok) { const err = await res.json().catch(()=>({})); throw new Error(err.detail || "Check failed"); }
      setReport(await res.json());
      toast.success("File analysed");
    } catch (e) { toast.error(e.message || "Check failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-6xl mx-auto p-8">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="overline">Integrity</div>
          <h1 className="font-display text-5xl mt-2 flex items-center gap-3"><ShieldCheck className="w-10 h-10 text-primary"/>Plagiarism &amp; Originality</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">Paste a proposal or research paper draft. We look for AI-like patterns, repeated boilerplate, and passages that echo common phrasing — then give a friendly score and actionable rewrite tips.</p>
        </div>
        {report && <Button variant="outline" className="rounded-full" onClick={()=>window.print()} data-testid="plag-print"><Printer className="w-4 h-4 mr-2"/>Print</Button>}
      </div>

      <div className="card-crisp rounded-lg p-6 mb-6">
        <div className="grid md:grid-cols-4 gap-3 mb-4">
          <div className="md:col-span-2">
            <Label>Document type</Label>
            <Tabs value={docType} onValueChange={setDocType}>
              <TabsList className="mt-2">
                <TabsTrigger value="proposal" data-testid="plag-type-proposal">Grant Proposal</TabsTrigger>
                <TabsTrigger value="paper" data-testid="plag-type-paper">Research Paper</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div>
            <Label>Model</Label>
            <Select value={String(modelIdx)} onValueChange={(v)=>setModelIdx(Number(v))}>
              <SelectTrigger data-testid="plag-model"><SelectValue/></SelectTrigger>
              <SelectContent>{MODELS.map((m,i)=><SelectItem key={i} value={String(i)}>{m.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button className="w-full rounded-full" onClick={run} disabled={loading || inputMode === "upload"} data-testid="plag-run">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Checking…</> : <><ScanSearch className="w-4 h-4 mr-2"/>Check originality</>}
            </Button>
          </div>
        </div>
        <Tabs value={inputMode} onValueChange={setInputMode} className="mb-3">
          <TabsList>
            <TabsTrigger value="paste" data-testid="plag-input-paste"><MessageSquare className="w-4 h-4 mr-1"/>Paste text</TabsTrigger>
            <TabsTrigger value="upload" data-testid="plag-input-upload"><Upload className="w-4 h-4 mr-1"/>Upload file</TabsTrigger>
          </TabsList>
          <TabsContent value="paste">
            <Label>Paste your draft</Label>
            <Textarea
              className="mt-2 min-h-[220px] font-mono text-sm"
              placeholder="Paste at least 60 characters — a section, an abstract, an introduction…"
              value={text}
              onChange={(e)=>setText(e.target.value)}
              spellCheck
              data-testid="plag-text"
            />
            <div className="mt-2 text-xs text-muted-foreground">{text.trim().split(/\s+/).filter(Boolean).length} words</div>
          </TabsContent>
          <TabsContent value="upload">
            <div
              className="mt-2 border-2 border-dashed border-primary/40 rounded-lg p-8 text-center hover:bg-primary/5 cursor-pointer transition-colors"
              onClick={()=>fileRef.current?.click()}
              onDragOver={(e)=>e.preventDefault()}
              onDrop={(e)=>{ e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) runFile(f); }}
              data-testid="plag-upload-area"
            >
              <FileText className="w-10 h-10 text-primary mx-auto mb-2"/>
              <div className="font-medium">{uploadName || "Drop your PDF / DOCX / TXT here"}</div>
              <div className="text-xs text-muted-foreground mt-1">Or click to browse — up to 20 MB</div>
              <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.md" hidden onChange={(e)=>{ const f = e.target.files?.[0]; if (f) runFile(f); }} data-testid="plag-upload-input"/>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {report && (
        <motion.div initial={{opacity:0, y:8}} animate={{opacity:1, y:0}} className="space-y-5" data-testid="plag-report">
          {(() => {
            const meta = FLAG_META(report.originality_score);
            const bg = meta.color === "emerald" ? "bg-emerald-50 border-emerald-300 text-emerald-900"
                     : meta.color === "amber" ? "bg-amber-50 border-amber-300 text-amber-900"
                     : "bg-red-50 border-red-300 text-red-900";
            const Ic = meta.Icon;
            return (
              <div className={`rounded-lg p-5 border-2 flex items-start gap-4 ${bg}`} data-testid="plag-verdict">
                <Ic className="w-10 h-10 shrink-0 mt-1"/>
                <div className="flex-1">
                  <div className="overline">Verdict</div>
                  <div className="font-display text-3xl leading-tight">{meta.label}</div>
                  <div className="text-sm mt-2"><b>Conclusion:</b> {report.summary || "This draft was evaluated across originality, AI-likeness and paraphrase-risk signals."}</div>
                  {report.human_touch_tips?.length > 0 && (
                    <div className="text-sm mt-2"><b>Suggestion:</b> {report.human_touch_tips[0]}</div>
                  )}
                </div>
                <div className="hidden sm:block text-right">
                  <div className="text-[10px] uppercase tracking-widest opacity-70">Originality</div>
                  <div className="font-display text-4xl leading-none">{report.originality_score}</div>
                  <div className="text-[10px]">/ 100</div>
                </div>
              </div>
            );
          })()}
          <div className="card-crisp rounded-lg p-6 grid md:grid-cols-3 gap-6 print:break-after-page">
            <ScoreRing score={report.originality_score} label="Originality" sublabel="higher is better"/>
            <ScoreRing score={report.ai_likelihood_score} label="AI-likeness" sublabel="lower is better"/>
            <ScoreRing score={report.paraphrase_risk_score} label="Paraphrase risk" sublabel="lower is better"/>
          </div>

          {report.summary && (
            <div className="card-crisp rounded-lg p-6">
              <div className="font-display text-2xl mb-2 flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary"/>Summary</div>
              <p className="text-sm">{report.summary}</p>
            </div>
          )}

          {report.flagged_passages?.length > 0 && (
            <div className="card-crisp rounded-lg p-6">
              <div className="font-display text-2xl mb-3 flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-600"/>Flagged passages</div>
              <div className="space-y-3">
                {report.flagged_passages.map((f,i)=>(
                  <div key={i} className="border-l-4 border-amber-400 pl-4 py-2 bg-amber-50/50 rounded-r-md">
                    <div className="text-sm italic">"{f.passage}"</div>
                    <div className="mt-1 text-xs text-amber-800"><b>Why:</b> {f.reason}</div>
                    {f.suggestion && <div className="mt-1 text-xs text-emerald-800"><b>Try:</b> {f.suggestion}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-3 gap-4">
            <BulletCard title="Stylometric notes" icon={Bot} color="violet" items={report.stylometric_notes}/>
            <BulletCard title="Reused / boilerplate phrases" icon={Repeat} color="amber" items={report.reused_phrases}/>
            <BulletCard title="Human-touch tips" icon={Lightbulb} color="emerald" items={report.human_touch_tips}/>
          </div>
        </motion.div>
      )}
    </div>
  );
}

const BULLET_COLORS = {
  violet:"border-violet-300 bg-violet-50 text-violet-950",
  amber:"border-amber-300 bg-amber-50 text-amber-950",
  emerald:"border-emerald-300 bg-emerald-50 text-emerald-950",
};
const BULLET_ICONS = { violet:"text-violet-700", amber:"text-amber-700", emerald:"text-emerald-700" };

function BulletCard({ title, icon: Icon, color, items }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div className={`rounded-lg p-5 border ${BULLET_COLORS[color]}`}>
      <div className="flex items-center gap-2 mb-3"><Icon className={`w-5 h-5 ${BULLET_ICONS[color]}`}/><div className="font-display text-lg">{title}</div></div>
      <ul className="space-y-1.5 text-sm">
        {items.map((s,i)=>(<li key={i} className="flex items-start gap-2"><span className={`mt-1 w-1.5 h-1.5 rounded-full ${BULLET_ICONS[color]} bg-current`}/><span>{s}</span></li>))}
      </ul>
    </div>
  );
}
